// ─────────────────────────────────────────────────────────────────────────────
// The phone's back gesture steps back INSIDE the app — closes a sheet, leaves a TODO — instead of
// leaving the app altogether.
//
// The app has no router: screens are store state. So the browser history carries a single TRAP
// entry while there is anything to go back from, rather than one entry per screen:
//
// - depth 0 → more: push the trap.
// - the back gesture (popstate): take ONE step back in the store (a sheet, the search card, a TODO); if there is still somewhere to
//   go back from, push the trap again.
// - more → 0 by the app's own hand (the header's back, a sheet's close button): pop the trap with
//   `history.back()`, and ignore the popstate that answers it.
//
// ⚠ One trap, not a stack, because the store can move between depths in ways no history stack
// mirrors — a sheet closed and another opened in one tick, a switch of installation that drops
// everything at once. With one entry the only thing to keep true is "there is a trap exactly while
// depth > 0", and the ignored popstate re-arms it when depth went back up in the meantime.
//
// Onboarding is out of scope: it has its own back button and pushes nothing here.
// ─────────────────────────────────────────────────────────────────────────────

import { type MobileState, type MobileStore } from "home/app/mobile/store";

/** How many back steps the screen has: an open sheet, the search card, an open TODO. 0 when signed out. */
export function navDepth(state: MobileState): number {
    if (!state.ready || !state.signedIn) return 0;
    let depth = 0;
    if (state.tab === "todos" && state.todoView === "detail") depth += 1;
    if (state.sheet) depth += 1;
    if (state.searchOpen) depth += 1;
    return depth;
}

export interface HistorySteps {
    /** Close the open sheet. */
    closeSheet(): void;
    /** Close the search card. */
    closeSearch(): void;
    /** Leave the TODO for the list. */
    back(): void;
}

const TRAP = { pqm: "back" } as const;

function isTrap(state: unknown): boolean {
    return Boolean(state && typeof state === "object" && (state as { pqm?: unknown }).pqm === TRAP.pqm);
}

/** The one popstate listener; module-level so a second init (the tests mount many times) replaces it. */
let popHandler: (() => void) | null = null;

export function initMobileHistory(store: MobileStore, steps: HistorySteps): void {
    if (popHandler) window.removeEventListener("popstate", popHandler);

    // ⚠ A RELOAD KEEPS THE HISTORY, trap included: the page comes back on the entry it was on. So
    // the trap may already be there, and pushing another would leave a dead entry behind per reload.
    let armed = isTrap(window.history.state);
    /** popstates still to come that answer our own history.back() — not the person's gesture. */
    let ignorePops = 0;
    /** True while a popstate is being turned into a store step, so that drop is not popped again. */
    let stepping = false;
    let depth = navDepth(store.getState());

    const arm = () => {
        window.history.pushState(TRAP, "");
        armed = true;
    };

    store.subscribe((state) => {
        const next = navDepth(state);
        const previous = depth;
        depth = next;
        if (stepping || ignorePops > 0) return;

        if (next > 0 && !armed) arm();
        else if (next === 0 && previous > 0 && armed) {
            armed = false;
            ignorePops += 1;
            window.history.back();
        }
    });

    popHandler = () => {
        if (ignorePops > 0) {
            ignorePops -= 1;
            // Depth went back up while our own pop was on its way: the trap it removed is needed.
            if (depth > 0 && !armed) arm();
            return;
        }

        armed = false;
        const state = store.getState();
        if (navDepth(state) === 0) return;

        stepping = true;
        try {
            if (state.sheet) steps.closeSheet();
            else if (state.searchOpen) steps.closeSearch();
            else steps.back();
        } finally {
            stepping = false;
        }
        if (depth > 0) arm();
    };
    window.addEventListener("popstate", popHandler);

    if (depth > 0) arm();
}
